import { ReactElement } from "react";
import { Navigate, RouteObject } from "react-router-dom";
import AlqemiRoutes from "../Alqemi/AlqemiRoutes";
import AlqemiArchiveRoutes from "../Alqemi/Archive/AlqemiArchiveRoutes";
import AlqemiAnalyticsRoutes from "../Alqemi/Analytics/AlqemiAnalyticsRoutes";
import AdminSettingsWrapper from "../pages/Settings/AdminSettings/AdminSettingsWrapper";
import ChangePassword from "../pages/Settings/Password/ChangePassword";
import UserSettingsWrapper from "../pages/Settings/UserSettingsWrapper";

// Simple route interface
export interface AppRoute {
  path: string;
  element: ReactElement;
  layout?: {
    heading?: string;
    showSecondarySidebar?: boolean;
    addToFavoriteVisible?: boolean;
    backToVisible?: boolean;
    useToolVisible?: boolean;
    signUpForNewsVisible?: boolean;
  };
}

// All routes in one place
export const routes: AppRoute[] = [
  {
    path: "/",
    element: <Navigate to="/alqemi" replace />,
    layout: {
      heading: "Alqemi",
      showSecondarySidebar: false,
    },
  },
  {
    path: "/alqemi/*",
    element: <AlqemiRoutes />,
    layout: {
      heading: "Alqemi",
      showSecondarySidebar: false,
    },
  },
  {
    path: "/archive/*",
    element: <AlqemiArchiveRoutes />,
    layout: {
      heading: "Alqemi - archive",
      showSecondarySidebar: false,
    },
  },
  {
    path: "/analytics/*",
    element: <AlqemiAnalyticsRoutes />,
    layout: {
      heading: "Alqemi - analytics",
      showSecondarySidebar: false,
    },
  },
  {
    path: "/user-settings",
    element: <UserSettingsWrapper />,
    layout: {
      heading: "User settings",
      showSecondarySidebar: false,
    },
  },
  {
    path: "/user-settings/change-password",
    element: <ChangePassword />,
    layout: {
      heading: "User settings",
      showSecondarySidebar: false,
    },
  },
  {
    path: "/admin-settings",
    element: <AdminSettingsWrapper />,
    layout: {
      heading: "Admin settings",
      showSecondarySidebar: false,
    },
  },
];

// Public paths that don't require authentication
export const publicPaths = [
  "/",
  "/register",
  "/forgot-password",
  "/verify-email",
];

// Convert to React Router format
export const routeObjects: RouteObject[] = routes.map(({ path, element }) => ({
  path,
  element,
}));

// Get layout config for current path
export const getLayoutForPath = (pathname: string) => {
  // Sort routes by path length (descending) to match more specific routes first
  const sortedRoutes = [...routes].sort((a, b) => {
    // Remove wildcards for comparison
    const aPath = a.path.replace("/*", "");
    const bPath = b.path.replace("/*", "");
    return bPath.length - aPath.length;
  });

  const route = sortedRoutes.find((r) => {
    const routePath = r.path.replace("/*", "");
    // Exact match or path starts with route path followed by /
    return pathname === routePath || pathname.startsWith(routePath + "/");
  });

  return {
    heading: route?.layout?.heading || "",
    secondarySidebarVisible: route?.layout?.showSecondarySidebar ?? true,
    addToFavoriteVisible: route?.layout?.addToFavoriteVisible ?? false,
    backToVisible: route?.layout?.backToVisible ?? true,
    useToolVisible: route?.layout?.useToolVisible ?? false,
    signUpForNewsVisible: route?.layout?.signUpForNewsVisible ?? false,
  };
};

// Module path utilities (for backward compatibility)
export const basePath = (moduleName: string) => `/modules/${moduleName}`;
export const dashboardPath = (moduleName: string) =>
  `${basePath(moduleName)}/dashboard`;
